import axios from 'axios'

// Same backend + admin/designer JWT as the ops portal — the design portal is just
// a second authenticated frontend on the shared API.
const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL || ''}/api`,
  timeout: 300000, // a 7-view Cycles render + cold start can approach a few minutes
})

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('adminToken')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      sessionStorage.removeItem('adminToken')
      window.location.href = '/login'
    }
    return Promise.reject(err)
  },
)

export const getMe = () => api.get('/admin/auth/me')

// ── Grasshopper Script Generator ─────────────────────────────────
// spec → Rhino 8 Python 3 component code (Claude runs server-side; key never ships here).
export const generateGhScript = (spec, extra) =>
  api.post('/gh/generate', { spec, extra })
// code → downloadable .gh bytes (503 until the Rhino-authored template is installed).
export const packageGhFile = (code) =>
  api.post('/gh/package', { code }, { responseType: 'arraybuffer' })
// List STL/OBJ/GLB meshes on Drive for a case (the "From case" picker).
export const getCaseMeshes = (caseId) => api.get(`/render/case/${caseId}/meshes`)
// Download a case mesh's bytes to the browser (for client-side rendering).
export const downloadCaseMesh = (caseId, fileId) =>
  api.get(`/render/case/${caseId}/mesh/${fileId}/download`, { responseType: 'arraybuffer' })
// Per-case DICOM metadata + quality mapped to the SSE report shape (ct_info + imaging).
export const getReportMeta = (caseId) => api.get(`/render/case/${caseId}/report-meta`)

// ── Admin — Cases ────────────────────────────────────────────────
export const getCases = (params) =>
  api.get('/admin/cases', { params })

// ── Admin — Models (GLB/STL) ─────────────────────────────────────
const CHUNK_SIZE = 20 * 1024 * 1024 // 20 MB — must match backend

export const uploadModel = async (caseId, file, description, onProgress) => {
  // Small files (≤ 25 MB): single-request upload
  if (file.size <= 25 * 1024 * 1024) {
    const formData = new FormData()
    formData.append('model', file)
    if (description) formData.append('description', description)
    return api.post(`/models/${caseId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
      onUploadProgress: onProgress
        ? (e) => onProgress(e.loaded, e.total)
        : undefined,
    })
  }

  // Large files: chunked upload (bypasses Cloud Run 32 MiB limit)
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE)

  // Step 1: Initiate chunked upload session
  const { data: { uploadId } } = await api.post(`/models/${caseId}/upload/init`, {
    filename: file.name,
    totalChunks,
    fileSize: file.size,
    description: description || '',
  })

  // Step 2: Send each chunk sequentially with smooth byte-level progress.
  // The last chunk's response blocks on backend STL→GLB conversion — we emit
  // `loaded === total` as soon as bytes are flushed, so callers can show a
  // "processing" state for the gap between 100% sent and HTTP response received.
  let totalSent = 0
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE
    const end = Math.min(start + CHUNK_SIZE, file.size)
    const chunkSize = end - start
    const chunk = file.slice(start, end)
    const chunkStart = totalSent

    const formData = new FormData()
    formData.append('chunk', chunk, `chunk-${i}`)

    const res = await api.post(`/models/upload/chunk/${uploadId}?index=${i}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      onUploadProgress: (e) => {
        if (onProgress && e.loaded != null) {
          const loaded = Math.min(e.loaded, chunkSize)
          onProgress(chunkStart + loaded, file.size)
        }
      },
    })

    totalSent = chunkStart + chunkSize
    if (onProgress) onProgress(totalSent, file.size)

    // Last chunk returns the created model
    if (res.status === 201) return res
  }
}

export default api
